<template>
  <app-page
    :title="$t('employee.reinvite.pageTitle')"
    :subtitle="
      employeesToReinviteCount > 0
        ? $t(
            employeesToReinviteCount === 1 ? 'employee.reinvite.subtitleSingular' : 'employee.reinvite.subtitlePlural',
            { count: employeesToReinviteCount },
          )
        : ''
    "
    icon="users"
  >
    <!-- Loading State -->
    <div v-if="isLoadingEmployees" class="text-center my-5">
      <b-spinner class="large-spinner" :label="$t('employee.reinvite.loadingLabel')"></b-spinner>
      <p class="mt-2">{{ $t('employee.reinvite.loadingEmployeesBody') }}</p>
    </div>

    <!-- Zero Employees to Reinvite State -->
    <div v-else-if="!isLoadingEmployees && employeesToReinviteCount === 0" class="text-center my-5">
      <h4>{{ $t('employee.reinvite.zeroTitle') }}</h4>
      <p>{{ $t('employee.reinvite.zeroBody') }}</p>
      <b-button variant="primary" :to="{ name: 'employees' }">{{
        $t('employee.reinvite.backToEmployeesButton')
      }}</b-button>
    </div>

    <!-- Main Content: Employees to Reinvite Available -->
    <div v-else-if="!isLoadingEmployees && employeesToReinviteCount > 0">
      <div>
        <b-row>
          <b-col md="8">
            <div class="form-field">
              <span class="field-label">{{ $t('employee.reinvite.subjectFieldLabel') }}</span>
              {{ $t('employee.reinvite.subjectLine') }}
            </div>
            <div class="form-field">
              <span class="field-label">{{ $t('employee.reinvite.fromFieldLabel') }}</span>
              {{ $t('employee.reinvite.fromLine', { companyName: state.company.name }) }}
            </div>
            <div class="form-field">
              <span class="field-label">{{ $t('employee.reinvite.toFieldLabel') }}</span>
              {{
                $t(
                  employeesToReinviteCount === 1
                    ? 'employee.reinvite.eligibleSingular'
                    : 'employee.reinvite.eligiblePlural',
                  {
                    count: employeesToReinviteCount,
                  },
                )
              }}
              <a href="#" class="ml-2" @click.prevent="showEmployeeDetailsModal = true">{{
                $t('employee.reinvite.seeDetailsLink')
              }}</a>
            </div>
          </b-col>
          <b-col md="4" class="text-right">
            <b-button
              variant="primary"
              @click="showConfirmSendModal = true"
              :disabled="isLoading || employeesToReinviteCount === 0"
            >
              <b-spinner small v-if="isLoading"></b-spinner>
              {{ $t('employee.reinvite.sendButton') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div class="mt-4">
        <h5>{{ $t('employee.reinvite.emailPreviewHeading') }}</h5>
        <div v-if="isLoadingHtml" class="text-center my-2">
          <b-spinner class="align-middle"></b-spinner>
        </div>
        <b-card v-else>
          <div v-safe-html:email="emailHtml"></div>
        </b-card>
      </div>
    </div>

    <!-- Employee Details Modal -->
    <b-modal v-model="showEmployeeDetailsModal" :title="$t('employee.reinvite.modalTitle')" size="lg" hide-footer>
      <b-table
        striped
        class="mb-0"
        table-class="table-minimal"
        responsive
        show-empty
        :items="employeesToReinvite"
        :fields="employeeTableFields"
        :busy="isLoadingEmployees"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>{{ $t('employee.reinvite.loadingLabel') }}</strong>
          </div>
        </template>
      </b-table>
      <b-button class="mt-3" block @click="showEmployeeDetailsModal = false">{{
        $t('employee.reinvite.closeButton')
      }}</b-button>
    </b-modal>

    <!-- Confirm Send Modal -->
    <b-modal
      v-model="showConfirmSendModal"
      :title="$t('employee.reinvite.confirmSendModalTitle')"
      @ok="sendReinvites"
      :ok-disabled="isLoading"
    >
      <p>{{ $t('employee.reinvite.confirmSendBody', { count: employeesToReinviteCount }) }}</p>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()" :disabled="isLoading">
          {{ $t('common.cancelButton') }}
        </b-button>
        <b-button variant="primary" @click="ok()" :disabled="isLoading">
          <b-spinner small v-if="isLoading"></b-spinner>
          {{ $t('employee.reinvite.sendButton') }}
        </b-button>
      </template>
    </b-modal>
  </app-page>
</template>

<script>
import ApiClient from '@/ApiClient';
import AppPage from '@/components/AppPage.vue';
import State from '@/state/State';
import useAnalytics from '@/state/composables/useAnalytics';
import i18n from '@/i18n';

export default {
  name: 'EmployeesReinvitePage',
  components: { AppPage },
  data() {
    return {
      state: State.state,
      employeesToReinvite: [],
      emailHtml: '',
      showEmployeeDetailsModal: false,
      showConfirmSendModal: false,
      isLoadingEmployees: false,
      isLoadingHtml: false,
      isSending: false,
    };
  },
  setup() {
    const { postAnalyticsEvent } = useAnalytics();
    return { postAnalyticsEvent };
  },
  computed: {
    employeesToReinviteCount() {
      return this.employeesToReinvite.length;
    },
    isLoading() {
      return this.isLoadingEmployees || this.isLoadingHtml || this.isSending;
    },
    employeeTableFields() {
      return [
        { key: 'full_name', label: this.$t('employee.assignments.columns.name'), sortable: true },
        { key: 'email', label: this.$t('employees.export.emailHeader'), sortable: true },
      ];
    },
  },
  mounted() {
    this.postAnalyticsEvent({
      intent: 'view_employees_reinvite',
      task: 'reinvite_employees',
      action: 'view',
    });
    this.fetchEmployeesToReinvite();
    this.fetchEmailHtml();
  },
  methods: {
    async fetchEmployeesToReinvite() {
      this.isLoadingEmployees = true;
      try {
        const data = await ApiClient.getEmployeesToReinvite(this.state.company.company_id);
        this.employeesToReinvite = data.employees;
      } catch (error) {
        console.error('Error fetching employees to reinvite:', error);
        this.employeesToReinvite = [];
      } finally {
        this.isLoadingEmployees = false;
      }
    },
    async fetchEmailHtml() {
      this.isLoadingHtml = true;
      try {
        const data = await ApiClient.getEmployeeReinviteHtml(this.state.company.company_id);
        this.emailHtml = data.html;
      } catch (error) {
        console.error('Error fetching email HTML:', error);
        this.emailHtml = `<p>${i18n.t('employee.reinvite.emailPreviewErrorBody')}</p>`;
      } finally {
        this.isLoadingHtml = false;
      }
    },
    async sendReinvites() {
      this.postAnalyticsEvent({
        intent: 'start_send_employees_reinvite',
        task: 'reinvite_employees',
        action: 'click',
      });
      this.isSending = true;
      this.showConfirmSendModal = false;
      try {
        await ApiClient.reinviteEmployees(this.state.company.company_id);
        this.postAnalyticsEvent({
          intent: 'complete_send_employees_reinvite',
          task: 'reinvite_employees',
          action: 'alert',
        });
        // Optionally: show success message, refresh data, etc.
        alert(i18n.t('employee.reinvite.sendSuccessAlert')); // Placeholder for a nicer notification
        this.$router.push({ name: 'employees' });
      } catch (error) {
        this.postAnalyticsEvent({
          intent: 'error_send_employees_reinvite',
          task: 'reinvite_employees',
          action: 'alert',
        });
        console.error('Error sending reinvites:', error);
        alert(i18n.t('employee.reinvite.sendErrorAlert')); // Placeholder for a nicer notification
      } finally {
        this.isSending = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.form-field {
  margin-bottom: 5px;
  .field-label {
    color: var(--palette-color-brand-primary);
    font-weight: bold;
  }
}
.large-spinner {
  width: 3rem;
  height: 3rem;
}
</style>
